import { useTheme } from "next-themes";
import { Button } from "./ui/button";
import { useEffect } from "react";

interface TermsOfServiceProps {
  onNavigate: (page: string) => void;
}

export function TermsOfService({ onNavigate }: TermsOfServiceProps) {
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }, []);

  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";

  const navigate = (url: string, page: string) => {
    window.history.pushState({}, "", url);
    onNavigate(page);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* HEADER */}
      <header className="border-b sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/", "landing")}
            className="flex items-center"
          >
            <img src={logoSrc} alt="Swimnerd Logo" className="h-10 w-auto" />
          </button>

          <nav className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/find-pool", "find-pool")}>
              Find Pool
            </Button>
            <Button variant="ghost" onClick={() => navigate("/blog", "blog")}>
              Blog
            </Button>
            <Button variant="ghost" onClick={() => navigate("/signin", "signin")}>
              Sign In
            </Button>
            <Button onClick={() => navigate("/signup", "signup")}>
              Get Started
            </Button>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden text-white">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,hsl(var(--primary)),hsl(var(--primary)/0.85))]" />
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <h1 className="text-5xl font-bold mb-6 max-w-3xl">
            Terms of Service
          </h1>
          <p className="text-lg text-white/90 max-w-2xl">
            Please read these terms carefully before using Swimnerd.
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <main className="flex-1 bg-background">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <p className="text-muted-foreground mb-8">
            Last updated: January 2026
          </p>

          <section className="space-y-8 text-gray-700 dark:text-gray-300 leading-8">
            <p>
              These Terms of Service govern your access to and use of Swimnerd.
              By accessing or using our platform, you agree to be bound by these
              terms.
            </p>

            <h2 className="text-2xl font-semibold">Acceptance of Terms</h2>
            <p>
              By creating an account or using Swimnerd, you confirm that you
              have read, understood, and agree to these Terms of Service.
            </p>

            <h2 className="text-2xl font-semibold">User Accounts</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You are responsible for maintaining account confidentiality</li>
              <li>You must provide accurate and up-to-date information</li>
              <li>You are responsible for all activities under your account</li>
            </ul>

            <h2 className="text-2xl font-semibold">Subscriptions & Payments</h2>
            <p>
              Certain features require a paid subscription. Payments are
              processed securely through third-party providers. Fees are
              non-refundable unless required by law.
            </p>

            <h2 className="text-2xl font-semibold">Acceptable Use</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Do not misuse or interfere with the platform</li>
              <li>Do not attempt unauthorized access</li>
              <li>Do not upload harmful or illegal content</li>
            </ul>

            <h2 className="text-2xl font-semibold">Intellectual Property</h2>
            <p>
              All content, trademarks, and software associated with Swimnerd
              are owned by Swimnerd and protected by applicable laws.
            </p>

            <h2 className="text-2xl font-semibold">Termination</h2>
            <p>
              We may suspend or terminate access to Swimnerd if these terms
              are violated or if required to comply with legal obligations.
            </p>

            <h2 className="text-2xl font-semibold">Limitation of Liability</h2>
            <p>
              Swimnerd is provided “as is.” We are not liable for indirect,
              incidental, or consequential damages arising from platform use.
            </p>

            <h2 className="text-2xl font-semibold">Changes to Terms</h2>
            <p>
              We may update these Terms of Service from time to time. Continued
              use of Swimnerd constitutes acceptance of the revised terms.
            </p>

            <h2 className="text-2xl font-semibold">Contact Us</h2>
            <p>
              If you have any questions regarding these Terms, contact us at{" "}
              <strong>support@swimnerd.com</strong>.
            </p>
          </section>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="border-t text-center py-8 text-sm text-muted-foreground">
        © {new Date().getFullYear()} Swimnerd. Built for smarter swimming.
      </footer>
    </div>
  );
}
